import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Phone, Mail, Globe, MapPin, Trash2, Edit3, Search,
  Truck, Wrench, Fuel, ShoppingBag, HardHat, Pipette, Hammer, Package, Box, Building2,
} from "lucide-react";
import { toast } from "sonner";

// Category metadata — single source of truth for label + icon + accent.
const CATS = [
  { key: "concrete", label: "Concrete", icon: Building2, accent: "bg-zinc-700" },
  { key: "asphalt", label: "Asphalt", icon: Truck, accent: "bg-zinc-900" },
  { key: "aggregate", label: "Aggregate", icon: Box, accent: "bg-yellow-700" },
  { key: "steel", label: "Steel", icon: Wrench, accent: "bg-zinc-600" },
  { key: "drainage", label: "Drainage", icon: Pipette, accent: "bg-blue-700" },
  { key: "plant_hire", label: "Plant hire", icon: Truck, accent: "bg-[#FF5F15]" },
  { key: "fuel", label: "Fuel", icon: Fuel, accent: "bg-green-700" },
  { key: "hardware", label: "Hardware", icon: Hammer, accent: "bg-orange-700" },
  { key: "tools", label: "Tools", icon: Wrench, accent: "bg-zinc-700" },
  { key: "ppe", label: "PPE / Safety", icon: HardHat, accent: "bg-red-700" },
  { key: "transport", label: "Transport", icon: Truck, accent: "bg-blue-900" },
  { key: "other", label: "Other", icon: Package, accent: "bg-zinc-500" },
];

const CAT_MAP = Object.fromEntries(CATS.map((c) => [c.key, c]));
const catMeta = (k) => CAT_MAP[k] || CAT_MAP.other;

// Strip everything except digits + leading "+" so tel: links work in iOS Safari.
const telHref = (raw) => {
  if (!raw) return null;
  const cleaned = String(raw).replace(/[^\d+]/g, "");
  return cleaned ? `tel:${cleaned}` : null;
};

const mailHref = (raw) => (raw ? `mailto:${raw}` : null);

const webHref = (raw) => {
  if (!raw) return null;
  const t = String(raw).trim();
  if (!t) return null;
  return t.startsWith("http") ? t : `https://${t}`;
};

export default function Suppliers() {
  const { user } = useAuth();
  const isOffice = user.role !== "field";
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/suppliers");
      setItems(Array.isArray(data) ? data : []);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, []);

  // Local filter — server already returns the org's suppliers; client-side
  // filtering keeps the search snappy without hammering the API.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((s) => {
      if (cat !== "all" && s.category !== cat) return false;
      if (!q) return true;
      const hay = [s.trading_name, s.contact_person, s.phone, s.email, s.notes, s.address]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, cat]);

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.trading_name}? This removes them from your phone book.`)) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Supplier removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    }
  };

  const startEdit = (s) => {
    setEditing(s);
    setOpen(true);
  };

  return (
    <div data-testid="suppliers-page" className="px-4 py-4 md:p-6 lg:p-10 max-w-7xl mx-auto pb-24 lg:pb-10">
      <PageHeader
        tag="/ Trade phone book"
        title="Suppliers"
        subtitle={
          isOffice
            ? "Your trade supplier database — concrete, plant hire, fuel cards, the lot. Tap to call from any device."
            : "Tap a supplier to call them straight from your phone."
        }
        action={
          isOffice ? (
            <Dialog
              open={open}
              onOpenChange={(o) => {
                setOpen(o);
                if (!o) setEditing(null);
              }}
            >
              <DialogTrigger asChild>
                <Button
                  onClick={() => setEditing(null)}
                  className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
                  data-testid="new-supplier-button"
                >
                  <Plus className="w-4 h-4 mr-2" /> New supplier
                </Button>
              </DialogTrigger>
              {open && (
                <SupplierForm
                  key={editing?.id || "new-supplier"}
                  initial={editing}
                  onDone={() => {
                    setOpen(false);
                    setEditing(null);
                    load();
                  }}
                />
              )}
            </Dialog>
          ) : null
        }
      />

      {/* Search + category filter */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, contact, phone, notes…"
            className="pl-9 rounded-sm border-zinc-400"
            data-testid="suppliers-search"
          />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="rounded-sm border-zinc-400 sm:w-56" data-testid="suppliers-category-filter">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATS.map((c) => (
              <SelectItem key={c.key} value={c.key} data-testid={`suppliers-cat-${c.key}`}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!loaded ? null : filtered.length === 0 ? (
        items.length === 0 ? (
          <EmptyState
            title="No suppliers yet"
            description={
              isOffice
                ? "Add your first supplier — concrete plant, fuel card provider, hardware account."
                : "The office hasn't added any suppliers yet."
            }
          />
        ) : (
          <EmptyState
            title="No matches"
            description="Try a different search or category."
          />
        )
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="suppliers-list">
          {filtered.map((s) => (
            <SupplierCard
              key={s.id}
              s={s}
              isOffice={isOffice}
              onEdit={() => startEdit(s)}
              onDelete={() => remove(s)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
//  CARD
// ============================================================
function SupplierCard({ s, isOffice, onEdit, onDelete }) {
  const meta = catMeta(s.category);
  const Icon = meta.icon;
  const phoneHref = telHref(s.phone);
  const mail = mailHref(s.email);
  const web = webHref(s.website);

  return (
    <div className="hard-card overflow-hidden" data-testid={`supplier-card-${s.id}`}>
      <div className={`h-1.5 w-full ${meta.accent}`} />
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`shrink-0 w-10 h-10 rounded-sm ${meta.accent} text-white flex items-center justify-center`}>
            <Icon className="w-5 h-5" strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{meta.label}</div>
            <h3 className="font-display font-extrabold uppercase tracking-tight text-base leading-tight truncate" data-testid={`supplier-name-${s.id}`}>
              {s.trading_name}
            </h3>
            {s.contact_person && (
              <div className="text-xs text-zinc-600 dark:text-zinc-300 mt-0.5 truncate">{s.contact_person}</div>
            )}
          </div>
          {isOffice && (
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={onEdit}
                className="p-1.5 rounded-sm text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 dark:hover:bg-zinc-800 dark:hover:text-white"
                data-testid={`supplier-edit-${s.id}`}
                aria-label="Edit supplier"
              >
                <Edit3 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="p-1.5 rounded-sm text-zinc-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                data-testid={`supplier-delete-${s.id}`}
                aria-label="Delete supplier"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Tap-to-actions */}
        <div className="mt-3 flex flex-wrap gap-2">
          {phoneHref && (
            <a
              href={phoneHref}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#FF5F15] text-white text-xs font-bold uppercase tracking-wider active:scale-95 transition-transform"
              data-testid={`supplier-call-${s.id}`}
            >
              <Phone className="w-3.5 h-3.5" />
              Call
            </a>
          )}
          {mail && (
            <a
              href={mail}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-zinc-300 dark:border-zinc-700 text-xs font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              data-testid={`supplier-email-${s.id}`}
            >
              <Mail className="w-3.5 h-3.5" />
              Email
            </a>
          )}
          {web && (
            <a
              href={web}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-zinc-300 dark:border-zinc-700 text-xs font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              data-testid={`supplier-web-${s.id}`}
            >
              <Globe className="w-3.5 h-3.5" />
              Web
            </a>
          )}
        </div>

        {/* Details */}
        <div className="mt-3 space-y-1.5 text-xs text-zinc-600 dark:text-zinc-300">
          {s.phone && (
            <div className="flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 shrink-0 text-zinc-400" />
              <span className="font-mono">{s.phone}</span>
            </div>
          )}
          {s.address && (
            <div className="flex items-start gap-1.5">
              <MapPin className="w-3.5 h-3.5 shrink-0 text-zinc-400 mt-0.5" />
              <span className="leading-tight">{s.address}</span>
            </div>
          )}
          {s.account_number && (
            <div className="text-[11px] font-mono text-zinc-500">
              Acct&nbsp;<span className="font-bold text-zinc-700 dark:text-zinc-200">{s.account_number}</span>
              {s.payment_terms && <span className="ml-2">· {s.payment_terms}</span>}
            </div>
          )}
          {s.notes && (
            <p className="text-xs text-zinc-700 dark:text-zinc-300 mt-2 leading-snug border-l-2 border-zinc-300 dark:border-zinc-700 pl-2 italic">
              {s.notes}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
//  FORM (create / edit)
// ============================================================
function SupplierForm({ initial, onDone }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    trading_name: initial?.trading_name || "",
    category: initial?.category || "concrete",
    contact_person: initial?.contact_person || "",
    phone: initial?.phone || "",
    email: initial?.email || "",
    website: initial?.website || "",
    address: initial?.address || "",
    account_number: initial?.account_number || "",
    payment_terms: initial?.payment_terms || "",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.trading_name.trim()) {
      toast.error("Trading name is required");
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/suppliers/${initial.id}`, form);
        toast.success("Supplier updated");
      } else {
        await api.post("/suppliers", form);
        toast.success("Supplier added");
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent
      className="rounded-sm max-w-lg max-h-[calc(100dvh-env(safe-area-inset-top,0px)-2rem)] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden p-0 top-[calc(env(safe-area-inset-top,0px)+1rem)] sm:top-[50%] translate-y-0 sm:translate-y-[-50%]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
      data-testid="supplier-form-dialog"
    >
      <div className="p-4 sm:p-6 overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">
            {isEdit ? "Edit supplier" : "New supplier"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Update the details below." : "Add a trade supplier to your phone book."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="space-y-4 mt-4 min-w-0 max-w-full overflow-x-hidden">
          <div>
            <Label className="label-tag">Trading name *</Label>
            <Input
              value={form.trading_name}
              onChange={set("trading_name")}
              placeholder="e.g. Higgins Group"
              className="mt-2 rounded-sm border-zinc-400"
              data-testid="supplier-name-input"
              required
            />
          </div>

          <div>
            <Label className="label-tag">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="supplier-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATS.map((c) => (
                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Contact person</Label>
              <Input
                value={form.contact_person}
                onChange={set("contact_person")}
                placeholder="e.g. Dave from dispatch"
                className="mt-2 rounded-sm border-zinc-400"
                data-testid="supplier-contact-input"
              />
            </div>
            <div>
              <Label className="label-tag">Phone</Label>
              <Input
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="021 555 1234"
                className="mt-2 rounded-sm border-zinc-400 font-mono"
                data-testid="supplier-phone-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="orders@supplier.co.nz"
                className="mt-2 rounded-sm border-zinc-400"
                data-testid="supplier-email-input"
              />
            </div>
            <div>
              <Label className="label-tag">Website</Label>
              <Input
                value={form.website}
                onChange={set("website")}
                placeholder="supplier.co.nz"
                className="mt-2 rounded-sm border-zinc-400"
                data-testid="supplier-website-input"
              />
            </div>
          </div>

          <div>
            <Label className="label-tag">Address</Label>
            <Input
              value={form.address}
              onChange={set("address")}
              placeholder="123 Industrial Rd, Penrose, Auckland"
              className="mt-2 rounded-sm border-zinc-400"
              data-testid="supplier-address-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Your account #</Label>
              <Input
                value={form.account_number}
                onChange={set("account_number")}
                placeholder="e.g. ACC-12345"
                className="mt-2 rounded-sm border-zinc-400 font-mono"
                data-testid="supplier-account-input"
              />
            </div>
            <div>
              <Label className="label-tag">Payment terms</Label>
              <Input
                value={form.payment_terms}
                onChange={set("payment_terms")}
                placeholder="20th of month"
                className="mt-2 rounded-sm border-zinc-400"
                data-testid="supplier-terms-input"
              />
            </div>
          </div>

          <div>
            <Label className="label-tag">Notes</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={set("notes")}
              placeholder="Gate code, ask-for, delivery hours…"
              className="mt-2 rounded-sm border-zinc-400"
              data-testid="supplier-notes-input"
            />
          </div>

          <DialogFooter
            className="gap-2 flex-col sm:flex-row sticky bottom-0 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 px-4 sm:px-6 pt-3 pb-4 bg-background border-t border-zinc-200 dark:border-zinc-800 z-10"
          >
            <Button
              type="submit"
              disabled={busy}
              className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold w-full sm:w-auto"
              data-testid="supplier-save-button"
            >
              {busy ? "Saving…" : (isEdit ? "Save changes" : "Add supplier")}
            </Button>
          </DialogFooter>
        </form>
      </div>
    </DialogContent>
  );
}
